import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import api from "@/src/utils";
import { signOut } from "@/src/features/userSlice";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import InputComponent from "@/src/components/Input/Input";
import { Button } from "@/src/components/Button/Button";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import apiRoutes from "@/src/apiRoutes";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const deleteState = z.object({
  password: z
    .string({
      required_error: "Password is required",
      invalid_type_error: "Password is required",
    })
    .min(6, { message: "Enter a valid password" }),
});

const DeleteAccount = () => {
  const { t } = useTranslation();
  const trans = (key: string) =>
    t(`app.settings.securityCenter.account.deleteAccount.${key}`);
  type deleteType = z.infer<typeof deleteState>;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<deleteType>({
    resolver: zodResolver(deleteState),
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const deleteaccount = async (data: deleteType) => {
    const res = await api.post(apiRoutes.deleteAccount, {
      password: data.password,
    });

    return res.data;
  };

  const { mutate } = useMutation({
    mutationKey: ["delete_account"],
    mutationFn: deleteaccount,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data?.info);
        dispatch(signOut());
        navigate("/");
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submit: SubmitHandler<deleteType> = (data) => {
    if (data?.password) {
      mutate(data);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(submit)}>
        <CardHeader>
          <CardTitle>{trans("title")}</CardTitle>
          <CardDescription>{trans("desc")}</CardDescription>
        </CardHeader>
        <CardContent className="md:w-1/2">
          <InputComponent
            type={"password"}
            {...register("password")}
            label={trans("form.password")}
            errors={errors.password?.message}
          />
        </CardContent>
        <CardHeader className=" bg-back p-2 flex flex-row justify-between items-center border-t rounded-b-lg">
          <div></div>
          <Button type="submit" variant={"warning"}>
            {trans("form.deleteButton")}
          </Button>
        </CardHeader>
      </form>
    </Card>
  );
};

export default DeleteAccount;
